import { Col, Form, Row, Select } from "antd";
import { FormInstance } from "antd/es/form";
import axios from "axios";
import CompanyCard from "containers/Admin/CreateOrEditEvent/Cards/CompanyCard";
import { ActiveMember } from "interfaces/activeMember";
import { useEffect, useState } from "react";
import { EditEventFormType, EventResponsibilityFormType, ResponsibilityType } from "../CreateOrEditEvent";

const { Option } = Select;

type Props = { form: FormInstance<EditEventFormType>; responsibilities: EventResponsibilityFormType[] };

/**
 * Component for displaying and selecting responsible members in the company card.
 * @param form - The form object.
 * @param event - The event object.
 */
const CompanyCardResponsibleMembers: React.FC<Props> = ({ form, responsibilities }) => {
  const formResponsibilitiesValue = Form.useWatch("responsibilities", form);

  const [activeMembers, setMembers] = useState<ActiveMember[]>([]);
  useEffect(() => {
    form.setFieldsValue({ responsibilities });
  }, [form, responsibilities]);

  useEffect(() => {
    axios.get<ActiveMember[]>("/internal/active-members/").then(response => setMembers(response.data));
  }, []);

  /**
   * Handles the change event for the main responsible select component.
   * Only changes responsibilities of type 1 (main responsible).
   */
  const onMainResponsibleChange = (value: number[]) => {
    const newResponsibilities = formResponsibilitiesValue
      .filter((r: EventResponsibilityFormType) => r.responsibility_type !== ResponsibilityType.MAIN_RESPONSIBLE)
      .concat(value.map(id => ({ responsibility_type: ResponsibilityType.MAIN_RESPONSIBLE, bl_member: id })));

    form.setFieldsValue({ responsibilities: newResponsibilities });
  };

  /**
   * Handles the change event for the meeting responsible select component.
   * Only changes responsibilities of type 3 (meeting responsible).
   */
  const onMeetingResponsibleChange = (value: number[]) => {
    const newResponsibilities = formResponsibilitiesValue
      .filter((r: EventResponsibilityFormType) => r.responsibility_type !== ResponsibilityType.MEETING_RESPONSIBLE)
      .concat(value.map(id => ({ responsibility_type: ResponsibilityType.MEETING_RESPONSIBLE, bl_member: id })));

    form.setFieldsValue({ responsibilities: newResponsibilities });
  };

  return (
    <>
      <CompanyCard title="Ansvarlige">
        <Row gutter={[30, 40]}>
          <Col md={12} sm={24} xs={24}>
            <Form.Item<EditEventFormType>
              name="responsibilities"
              label="Hovedansvarlig"
              getValueProps={i => {
                return {
                  value: i
                    .filter(
                      (r: EventResponsibilityFormType) => r.responsibility_type === ResponsibilityType.MAIN_RESPONSIBLE
                    )
                    .map((r: EventResponsibilityFormType) => r.bl_member),
                };
              }}
            >
              <Select
                onChange={onMainResponsibleChange}
                mode="multiple"
                placeholder="Velg HA"
                style={{ width: "95%", margin: "2px 0 0 0" }}
                showSearch
                filterOption={(input, option) => {
                  if (!input) return true;
                  const optionText = option?.children?.toString().toLowerCase() ?? "";
                  return optionText.includes(input.toLowerCase());
                }}
              >
                {activeMembers.map(member => {
                  return (
                    <Option value={member.id} key={member.id}>
                      {member.full_name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <Form.Item<EditEventFormType>
              name="responsibilities"
              label="Møtepliktig"
              getValueProps={i => {
                return {
                  value: i
                    .filter(
                      (r: EventResponsibilityFormType) =>
                        r.responsibility_type === ResponsibilityType.MEETING_RESPONSIBLE
                    )
                    .map((r: EventResponsibilityFormType) => r.bl_member),
                };
              }}
            >
              <Select
                onChange={onMeetingResponsibleChange}
                mode="multiple"
                placeholder="Velg MP"
                style={{ width: "95%", margin: "2px 0 0 0" }}
                showSearch
                filterOption={(input, option) => {
                  if (!input) return true;
                  const optionText = option?.children?.toString().toLowerCase() ?? "";
                  return optionText.includes(input.toLowerCase());
                }}
              >
                {activeMembers.map(member => {
                  return (
                    <Option value={member.id} key={member.id}>
                      {member.full_name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </CompanyCard>
    </>
  );
};

export default CompanyCardResponsibleMembers;
